import { useState, useCallback, useMemo } from 'react';
import { SearchItem, SearchResults } from '../../../types';
import {
	getMetapropertyOptionsFromSearchResults,
	getTagsAsSerchItemsFromSearchResults,
} from '../helpers/searchUtils';

interface Props {
	searchResults: SearchResults;
	isFetchingAssets: boolean;
	showDropdownResults: boolean;
	handleEnterKey: () => void;
	setShowDropdownResults: (show: boolean) => void;
	setSearchTerm: (term: string) => void;
}

export const useSearchItemKeyboardNavigation = ({
	searchResults,
	isFetchingAssets,
	showDropdownResults,
	handleEnterKey,
	setShowDropdownResults,
	setSearchTerm,
}: Props) => {
	const [currentActiveSearchItem, setCurrentActiveSearchItem] = useState<SearchItem | undefined>(
		undefined,
	);

	const iterableItemsFromResults = useMemo(() => {
		// This function groups only the items from the search results, aka the things that are displayed in the dropdown
		// We mainly exclude the counts and for tags we need to transorm them into objects to be consistent
		// For metaproperties, the value is an array of metaproperties but we only want the options because we navigate over options
		if (!searchResults) {
			return [];
		}

		const { assets, tags, metaproperties, collections, guidelines, pages } = searchResults;

		// tags come as an array with label and count ["tag1", 4, "tag2", 7], we only want the string values
		// We need to build them into an object to be consistent with other search items
		const tagsAsSearchItems = getTagsAsSerchItemsFromSearchResults(tags);
		const metapropertyOptions = getMetapropertyOptionsFromSearchResults(metaproperties || []);

		const itemsToNavigateOver = [
			// NOTE: The order is important here, as it defines the navigation order
			assets.items,
			tagsAsSearchItems,
			metapropertyOptions,
			collections.items,
			guidelines.items,
			pages.items,
		].flat();

		return itemsToNavigateOver;
	}, [searchResults]);

	const handleArrowKeyNavigation = useCallback(
		(direction: 'up' | 'down') => {
			if (iterableItemsFromResults.length === 0) {
				return;
			}

			let nextIndex: number;

			if (currentActiveSearchItem === undefined) {
				nextIndex = 0;
			} else {
				const currentIndex = iterableItemsFromResults.findIndex(
					(item) => item?.id === currentActiveSearchItem.id,
				);

				if (direction === 'down') {
					nextIndex =
						currentIndex === -1
							? 0
							: Math.min(currentIndex + 1, iterableItemsFromResults.length - 1);
				} else {
					nextIndex =
						currentIndex === -1
							? iterableItemsFromResults.length - 1
							: Math.max(currentIndex - 1, 0);
				}
			}

			setCurrentActiveSearchItem(iterableItemsFromResults[nextIndex]);
		},
		[iterableItemsFromResults, currentActiveSearchItem],
	);

	const onInputKeyUp = useCallback(
		(e: React.KeyboardEvent) => {
			e.stopPropagation();
			e.preventDefault();

			if (!showDropdownResults) {
				setCurrentActiveSearchItem(undefined);
			}

			if (e.key === 'Enter') {
				if (isFetchingAssets) {
					// We block new asset fetch requests while one is happening to prevent race conditions
					return;
				}

				handleEnterKey();

				return;
			}

			switch (e.key) {
				case 'ArrowDown': {
					handleArrowKeyNavigation('down');
					break;
				}

				case 'ArrowUp': {
					handleArrowKeyNavigation('up');
					break;
				}

				case 'Escape':
					setShowDropdownResults(false);
					setCurrentActiveSearchItem(undefined);
					setSearchTerm('');
					break;
			}
		},
		[
			handleArrowKeyNavigation,
			handleEnterKey,
			isFetchingAssets,
			setSearchTerm,
			setShowDropdownResults,
			showDropdownResults,
		],
	);

	return {
		currentActiveSearchItem,
		onInputKeyUp,
		setCurrentActiveSearchItem,
	};
};
